import { NbDialogConfig, NbDialogRef } from '@nebular/theme';
import { Observable } from 'rxjs';

/**
 * @description Представляє дані, необхідні для відкриття діалогового вікна для метода open.
 * @property {string} [id] - Необов'язковий унікальний ідентифікатор для діалогового вікна.
 * @property {function} [refCb] - Необов'язкова функція зворотного виклику, яка викликається з посиланням на діалогове вікно, коли воно створюється та закривається.
 * @property {Partial<NbDialogConfig>} [config] - Необов'язкова конфігурація для діалогового вікна на основі NbDialogConfig від Nebular.
 * @property {Promise<any>} component - Проміс, котрий повертає компонент, що буде відображено всередині діалогового вікна.
 * @property {boolean} [successOnly] - Якщо true, діалогове вікно буде вирішено лише після успішного підтвердження, ігноруючи скасування.
 * @property {boolean} [isObservable] - Якщо true, вказує, що результат діалогового вікна є Observable, інакше Subscription.
 */
export type TDialogData = {
  id?: string;
  refCb?: (ref: NbDialogRef<unknown> | null, dialogId: string) => void;
  config?: Partial<NbDialogConfig>;
  component: Promise<any>;
  successOnly?: boolean;
  isObservable?: boolean;
};

/**
 * @description Представляє дані для зв'язаного діалогового вікна, які можуть бути відкриті після іншого діалогу.
 * @property {function} [openFn] - Необов'язкова функція, яка виконується для відкриття наступного діалогу на основі відповіді поточного.
 * @property {function} [mapDataFn] - Необов'язкова функція для трансформації даних відповіді поточного діалогу для наступного.
 * @property {function} [canOpenFn] - Необов'язкова функція для перевірки можливості відкриття наступного діалогу.
 * За замовчуванням для prev діалогу перевіряє чи тип відповіді EDialogAnswer.Close, для next EDialogAnswer.Confirm.
 * @property {string} id - Унікальний ідентифікатор цього діалогового вікна.
 */
export interface IILinkedNextDialogData<T, R = T, MapFn = T> {
  openFn?: (dialogAnswer: TDialogAnswerPayload<T>) => Observable<TDialogAnswerPayload<R>>;
  mapDataFn?: (dialogAnswer: TDialogAnswerPayload<T>) => ILinkedDialogData<T, MapFn, R, R>;
  canOpenFn?: (dialogAnswer: TDialogAnswerPayload<T>) => boolean;
  id: string;
}

/**
 * @description Представляє дані для зв'язаного діалогового вікна з можливістю навігації між діалогами.
 * @property {function} [mapDataFn] - Необов'язкова функція для трансформації даних відповіді цього діалогу.
 * @property {IILinkedNextDialogData} [prev] - Необов'язкові дані попереднього діалогу.
 * @property {IILinkedNextDialogData} [next] - Необов'язкові дані наступного діалогу.
 * @property {string} id - Унікальний ідентифікатор цього діалогового вікна.
 */
export interface ILinkedDialogData<T = unknown, MapFn = T, Next = T, Prev = T>
  extends Omit<TDialogData, 'isObservable'> {
  mapDataFn?: (dialogAnswer: TDialogAnswerPayload<T>) => TDialogAnswerPayload<MapFn>;
  prev?: IILinkedNextDialogData<T, Prev>;
  next?: IILinkedNextDialogData<T, Next>;
  id: string;
}

export enum EDialogAnswer {
  Confirm = 'confirm',
  Cancel = 'cancel',
}

export type TDialogAnswerPayload<T = unknown> =
  | {
      type: EDialogAnswer;
      payload?: T;
    }
  | undefined;
